import React, { useEffect, useRef, useState } from 'react';
import { styled } from 'styled-components';
import PageTop from '../../components/pageTop/PageTop';
import { useNavigate } from 'react-router-dom';
import { isLogin } from '../../../utils/auth';
import mySearchOptionService from '../../../service/mySearchOption/mySearchOption';
import OptionList from './OptionList';
import DeleteModal from './DeleteModal';
import { FilterType } from '../FilterData';
import SaveFilter from '../saveFilter/SaveFilter';

interface OptionType {
  count: number;
  date: string;
  p_idx: number;
  param: string;
  save_type: string;
  title: string;
}

function MySearchOption() {
  const navigate = useNavigate();
  const [recentFilters, setRecentFilters] = useState<FilterType | null>(null);
  const [openSave, setOpenSave] = useState(false);
  const [recentOption, setRecentOption] = useState<OptionType | null>(null);
  const [optionList, setOptionList] = useState<OptionType[]>([]);
  const [openMenuIdx, setOpenMenuIdx] = useState<number | null>(null);
  const [selectDeleteOption, setSelectDeleteOption] =
    useState<OptionType | null>(null);

  const menuRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        menuRef.current &&
        !(menuRef.current as HTMLElement).contains(e.target as Node)
      ) {
        setOpenMenuIdx(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getSearchOptionList = async (type: string) => {
    try {
      if (!isLogin()) return;
      const res = await mySearchOptionService.list(type);

      const recent =
        res.data.find((item: OptionType) => item.save_type === 'N') || null;
      const savedList = res.data.filter(
        (item: OptionType) => item.save_type !== 'N',
      );
      setOptionList(savedList);
      setRecentOption(recent);
    } catch (error) {
      console.error('getSearchOptionList 함수에서 에러 발생:', error);
    }
  };

  const param = new URLSearchParams(window.location.search);
  const type = param.get('type') ?? 'a';

  useEffect(() => {
    getSearchOptionList(type);
  }, [type]);

  const parseRegion = (region: string) => {
    const [sido, sigungu, emd] = region.trim?.().split(' ');
    return [
      {
        sido: sido || '',
        sigungu: sigungu || '',
        emd: emd || '',
      },
    ];
  };

  const convertArray = (data: string) => {
    if (!data) return [];
    if (data.split(',').length === 0) return [];
    return data.split(',');
  };

  const saveRecentSearch = (param: string) => {
    const data = JSON.parse(param);

    const filters = {
      type: data.type,
      year: data?.search2?.trim?.().split('타경')[0] || '',
      caseNumber:
        data?.type === 'p'
          ? data?.search2
          : data?.search2?.trim?.().split('타경')[1] || '',
      regionOrCourt: data?.isCourtOrRegion,
      region: parseRegion(data.region),
      address: data.search,
      court: data?.isCourtOrRegion !== 'r' ? data?.court : '',
      itemStatus: convertArray(data.totalCategory),
      multiItemStatus: [],
      appraisalPrice: {
        min:
          data?.minappraisedValue?.trim?.() === '0'
            ? ''
            : data?.minappraisedValue?.trim?.() || 0,
        max:
          data?.maxappraisedValue?.trim?.() === '0'
            ? ''
            : data?.maxappraisedValue?.trim?.() || 0,
      },
      minPrice: {
        min:
          data?.minBidPrice?.trim?.() === '0'
            ? ''
            : data?.minBidPrice?.trim?.() || 0,
        max:
          data?.maxBidPrice?.trim?.() === '0'
            ? ''
            : data?.maxBidPrice?.trim?.() || 0,
      },
      approvalDate: {
        start: data?.minApprovalDate?.trim?.() || '',
        end: data?.maxApprovalDate?.trim?.() || '',
      },
      saleDate: {
        start: data?.minSaleDate?.trim?.() || '',
        end: data?.maxSaleDate?.trim?.() || '',
      },
      buildingArea: {
        min:
          data?.minconstructArea?.trim?.() === '0'
            ? ''
            : data?.minconstructArea?.trim?.(),
        max:
          data?.maxconstructArea?.trim?.() === '0'
            ? ''
            : data?.maxconstructArea?.trim?.(),
      },
      landArea: {
        min:
          data?.minlandArea?.trim?.() === '0'
            ? ''
            : data?.minlandArea?.trim?.(),
        max:
          data?.maxlandArea?.trim?.() === '0'
            ? ''
            : data?.maxlandArea?.trim?.(),
      },
      bidno: data.bidno,
      progressStatus: data.bidStatus,
      useArea: convertArray(data.zoning),
      multiUseArea: [],
      specialConditionViewOption: data.specialItemCond,
      specialItemCondition: convertArray(data.specialItem),
      multiSpecialItemCondition: [],
      holdingCompany: [],
      multiHoldingCompany: [],
    };
    setRecentFilters(filters);
    setOpenSave(true);
  };

  const handleSearch = (param: string) => {
    const data = JSON.parse(param);

    const filters = {
      type: data.type,
      year: data?.search2?.trim?.().split('타경')[0] || '',
      caseNumber:
        data?.type === 'p'
          ? data?.search2
          : data?.search2?.trim?.().split('타경')[1] || '',
      regionOrCourt: data?.isCourtOrRegion,
      region: parseRegion(data.region),
      address: data.search,
      court: data?.isCourtOrRegion !== 'r' ? data?.court : '',
      itemStatus: convertArray(data.totalCategory),
      multiItemStatus: [],
      appraisalPrice: {
        min:
          data?.minappraisedValue?.trim?.() === '0'
            ? ''
            : data?.minappraisedValue?.trim?.() || 0,
        max:
          data?.maxappraisedValue?.trim?.() === '0'
            ? ''
            : data?.maxappraisedValue?.trim?.() || 0,
      },
      minPrice: {
        min:
          data?.minBidPrice?.trim?.() === '0'
            ? ''
            : data?.minBidPrice?.trim?.() || 0,
        max:
          data?.maxBidPrice?.trim?.() === '0'
            ? ''
            : data?.maxBidPrice?.trim?.() || 0,
      },
      approvalDate: {
        start: data?.minApprovalDate?.trim?.() || '',
        end: data?.maxApprovalDate?.trim?.() || '',
      },
      saleDate: {
        start: data?.minSaleDate?.trim?.() || '',
        end: data?.maxSaleDate?.trim?.() || '',
      },
      buildingArea: {
        min:
          data?.minconstructArea?.trim?.() === '0'
            ? ''
            : data?.minconstructArea?.trim?.(),
        max:
          data?.maxconstructArea?.trim?.() === '0'
            ? ''
            : data?.maxconstructArea?.trim?.(),
      },
      landArea: {
        min:
          data?.minlandArea?.trim?.() === '0'
            ? ''
            : data?.minlandArea?.trim?.(),
        max:
          data?.maxlandArea?.trim?.() === '0'
            ? ''
            : data?.maxlandArea?.trim?.(),
      },
      bidno: data.bidno,
      progressStatus: data.bidStatus,
      useArea: convertArray(data.zoning),
      multiUseArea: [],
      specialConditionViewOption: data.specialItemCond,
      specialItemCondition: convertArray(data.specialItem),
      multiSpecialItemCondition: [],
      holdingCompany: [],
      multiHoldingCompany: [],
    };
    navigate(
      `/search?type=e&itemType=${filters.type}&regionOrCourt=${filters.regionOrCourt}&options=${JSON.stringify(filters)}`,
    );
  };

  return (
    <Container>
      <PageTop text="내 검색조건" />
      <Inner>
        {recentOption && (
          <Card recent>
            <CardHeader>
              <CardTitle>
                {recentOption.date}
                <Title>
                  <img src="/assets/images/icon/history.svg" alt="" />
                  최근검색
                </Title>
              </CardTitle>
              <TextButton
                color="#008AFF"
                onClick={() => saveRecentSearch(recentOption.param)}
              >
                저장하기
              </TextButton>
            </CardHeader>
            <OptionList data={recentOption.param} />
            <SearchButtonBox>
              <SearchButton
                disabled={recentOption.count === 0}
                onClick={() => {
                  handleSearch(recentOption.param);
                }}
              >
                <span>{recentOption.count}</span>건 바로검색
                <img
                  src={`/assets/images/arrow/${recentOption.count === 0 ? 'arrow-right-disabled' : 'arrow-right'}.svg`}
                  alt=""
                />
              </SearchButton>
            </SearchButtonBox>
          </Card>
        )}
        <SavedOptionList>
          저장된 검색조건
          {optionList.map((option) => (
            <Card key={option.p_idx}>
              <CardHeader>
                <CardTitle>
                  {option.date}
                  <Title>{option.title}</Title>
                </CardTitle>
                <img
                  src="/assets/images/icon/more.svg"
                  alt=""
                  onClick={() =>
                    setOpenMenuIdx(
                      openMenuIdx === option.p_idx ? null : option.p_idx,
                    )
                  }
                />
                {openMenuIdx === option.p_idx && (
                  <MenuBox ref={menuRef}>
                    <PopupMenu
                      onClick={() => {
                        setSelectDeleteOption(option);
                        setOpenMenuIdx(null);
                      }}
                    >
                      <img src="/assets/images/icon/trashcan-red.svg" alt="" />
                      삭제하기
                    </PopupMenu>
                  </MenuBox>
                )}
              </CardHeader>
              <OptionList data={option.param} />
              <SearchButtonBox>
                <SearchButton
                  disabled={option.count === 0}
                  onClick={() => {
                    handleSearch(option.param);
                  }}
                >
                  <span>{option.count}</span>건 바로검색
                  <img
                    src={`/assets/images/arrow/${option.count === 0 ? 'arrow-right-disabled' : 'arrow-right'}.svg`}
                    alt=""
                  />
                </SearchButton>
              </SearchButtonBox>
            </Card>
          ))}
        </SavedOptionList>
      </Inner>
      {selectDeleteOption && (
        <DeleteModal
          option={selectDeleteOption}
          getSearchOptionList={() => getSearchOptionList(type)}
          onClose={() => setSelectDeleteOption(null)}
        />
      )}
      {openSave && recentFilters && (
        <SaveFilter
          filters={recentFilters}
          setFilters={setRecentFilters}
          onClose={() => {
            setOpenSave(false);
            setRecentFilters(null);
            getSearchOptionList(type);
          }}
        />
      )}
    </Container>
  );
}
export default MySearchOption;

const Container = styled.div`
  max-width: 900px;
  min-height: 100vh;
  margin: 0 auto;
  box-shadow: 0 0 20px hsla(0, 0%, 51%, 0.15);
  position: relative;
`;

const Inner = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px 16px;
`;

const Card = styled.div<{ recent?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  background-color: ${({ recent }) => recent && '#F7F8FC'};

  width: 100%;
  border-radius: 16px;
  border: ${({ recent }) => !recent && '1px solid #e1e2e4'};
  box-shadow: ${({ recent }) =>
    !recent &&
    `0px 0px 1px 0px #00000014,
       0px 0px 1px 0px #00000014,
       0px 1px 2px 0px #0000001f`};
`;

const CardHeader = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  position: relative;
`;

const Title = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  font-weight: 700;
  font-size: 16px;
  line-height: 145%;
  letter-spacing: -2%;
  color: #222222;
`;

const CardTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;

  font-weight: 500;
  font-size: 12px;
  line-height: 145%;
  letter-spacing: -2%;
  color: #37383c9c;
`;

const TextButton = styled.div<{ color: string }>`
  font-family: Spoqa Han Sans Neo;
  font-weight: 500;
  font-size: 14px;
  line-height: 145%;
  letter-spacing: -2%;
  color: ${({ color }) => color};
`;

const SearchButtonBox = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`;

const SearchButton = styled.button<{ disabled?: boolean }>`
  display: flex;
  flex-direction: row;
  gap: 2px;
  align-items: center;
  padding: 8px 16px;
  border-radius: 24px;
  border: 1px solid #e1e2e4;
  background-color: ${({ disabled }) => (disabled ? '#F7F7F8' : '#ffffff')};
  color: ${({ disabled }) => (disabled ? '#37383C29' : '#222')};

  font-weight: 500;
  font-size: 14px;
  line-height: 145%;
  letter-spacing: -2%;

  span {
    color: ${({ disabled }) => (disabled ? '#37383C29' : '#008aff')};
  }
`;

const SavedOptionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  font-family: Spoqa Han Sans Neo;
  font-weight: 500;
  font-size: 14px;
  line-height: 145%;
  letter-spacing: -2%;
  color: #616161;
`;

const MenuBox = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  background-color: #ffffff;
  border-radius: 8px;
  padding: 4px;
  border: 1px solid #e1e2e4;
  box-shadow: 0px 4px 15px 0px #8d95a340;
  z-index: 100;
`;

const PopupMenu = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  border-radius: 4px;
  padding: 8px;

  font-weight: 500;
  font-size: 14px;
  line-height: 145%;
  letter-spacing: -2%;
  color: #d84836;
`;
